import { SharedGridStyleBaseColor } from "features/CenterContent/shared/MainContentGrid";
import { Itinerary } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/Itinerary";
import { VehicleCancellation } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleCancellation/VehicleCancellation";
import { VehicleUndoCancellation } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleCancellation/VehicleUndoCancellation";
import { VehiclePhoneNumber } from "features/CenterContent/VehicleDetails/shared/VehiclePhoneNumber";
import { useMemo } from "react";
import { ExpandableInfoMessage } from "shared/components/feedback/InfoMessage/ExpandableInfoMessage/ExpandableInfoMessage";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import { Module } from "shared/components/Module";
import { BusGps } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/BusGps/BusGps";
import OperationalLog from "features/CenterContent/VehicleDetails/shared/OperationalLog";
import { Box, Text, VStack } from "shared/components/Spor";
import {
  AlternativeTransportItinerary,
  AlternativeTransportItineraryStop,
  AlternativeTransportVehicle,
  StopReference,
} from "shared/types/alternativeTransport";
import styled, { useTheme } from "styled-components";
import { IncidentsAndStopsPanels } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/IncidentsAndStopsPanels";
import {
  ActionsKebabMenu,
  InlineActions,
} from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/ActionsOverflowMenu";

const BusModule = styled(Module).attrs({ size: "lg" })`
  height: 100%;
  min-height: 0;
  && {
    border: 1px solid ${({ theme }) => theme.colorOutlineSecondary};
    border-left: none;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
`;

const HorizontalScroll = styled.div`
  flex: 1;
  min-height: 0;
  margin-right: -15px;
  display: flex;
  flex-direction: column;
  overflow-x: auto;
  overflow-y: hidden;
`;

const ScrollContent = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 809px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @container altTransportPage (width < 1696px) {
    min-width: 0;
  }
`;

const PanelScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 18px;
  padding-right: 15px;
`;

const ModuleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(400px, 812px));
  gap: 12px;

  @container altTransportPage (width < 1696px) {
    grid-template-columns: 1fr;
  }
`;

const ScrollModuleGrid = styled(ModuleGrid)`
  align-items: start;
  padding-bottom: 50px;
`;

interface AlternativeTransportLeftPanelProps {
  vehicle: AlternativeTransportVehicle;
}

export const AlternativeTransportLeftPanel = ({
  vehicle,
}: AlternativeTransportLeftPanelProps) => {
  const theme = useTheme();
  const trainNumberTurnicate =
    vehicle.trainIds.join(", ").length > 16
      ? `${vehicle.trainIds.join(", ").substring(0, 16)}...`
      : vehicle.trainIds.join(", ");

  const getDistributionStopPlace = (
    itinerary: AlternativeTransportItinerary | null,
  ) =>
    itinerary
      ? [...itinerary.stops, ...(itinerary.returnStops ?? [])]
          .filter((stop) => !!stop.stopReference.nsrStopPlace)
          .map((stop) => stop.stopReference.nsrStopPlace)
          .join(",")
      : "";

  const distributionStopReferences: Array<StopReference> = useMemo(() => {
    const stops = vehicle?.itinerary?.stops ?? [];
    const returnStops = vehicle?.itinerary?.returnStops ?? [];
    return [...stops, ...returnStops].map(
      (value: AlternativeTransportItineraryStop) => value.stopReference,
    );
  }, [vehicle]);

  const distributionStopPlaces = useMemo(
    () => getDistributionStopPlace(vehicle.itinerary),
    [vehicle],
  );

  const shiftId = vehicle.workShiftPlan?.shiftId;
  const departureNumber = `${shiftId ? `${shiftId}-` : ""}${trainNumberTurnicate}${
    trainNumberTurnicate && !shiftId ? " Operative" : ""
  }`;

  const specification = vehicle.isPartOfShuttlePlan ? "Pendel" : undefined;

  return (
    <BusModule
      title={
        <>
          <Box as="span" whiteSpace="nowrap">
            Avgang:
          </Box>
          {departureNumber ? (
            <>
              {" "}
              <Box as="span" whiteSpace="nowrap">
                {departureNumber}
              </Box>
            </>
          ) : null}
        </>
      }
      specification={specification}
      action1={
        <>
          <ActionsKebabMenu>
            <VehiclePhoneNumber
              phoneNumberUpdate={vehicle.driverPhoneNumber}
              workShift={vehicle.workShiftPlan}
              id={vehicle.id}
              operatorUpdate={{
                operatorName: vehicle.operatorName,
                operatorId: vehicle.operatorId,
              }}
            />
            <VehicleCancellation vehicle={vehicle} />
            <VehicleUndoCancellation vehicle={vehicle} />
          </ActionsKebabMenu>
          <InlineActions>
            <VehiclePhoneNumber
              phoneNumberUpdate={vehicle.driverPhoneNumber}
              workShift={vehicle.workShiftPlan}
              id={vehicle.id}
              operatorUpdate={{
                operatorName: vehicle.operatorName,
                operatorId: vehicle.operatorId,
              }}
            />
          </InlineActions>
        </>
      }
    >
      <Box
        flexShrink="0"
        pt="18px"
        pb="18px"
        mx="-18px"
        px="18px"
        borderBottomWidth="1px"
        borderBottomStyle="solid"
        borderBottomColor={theme.colorOutlineSecondary}
      >
        <Itinerary alternativeTransport={vehicle} />
      </Box>
      <HorizontalScroll>
        <ScrollContent>
          <PanelScrollArea>
            <ScrollModuleGrid>
              <Module title="Trasé" disablePadding>
                <IncidentsAndStopsPanels
                  vehicle={vehicle}
                  distributionStopReferences={distributionStopReferences}
                />
              </Module>
              <VStack gap="12px" align="stretch">
                <Box flexShrink="0">
                  <Module title="Info fra Brudd">
                    {vehicle.notes.length > 0 ? (
                      <div style={{ width: "100%" }}>
                        <ExpandableInfoMessage
                          semantics="info"
                          title="Info fra Brudd"
                        >
                          {vehicle.notes.map((note) => (
                            <Text key={note}>{note}</Text>
                          ))}
                        </ExpandableInfoMessage>
                      </div>
                    ) : (
                      <NoDataMessage
                        panelStyle={SharedGridStyleBaseColor}
                        reason="Det finnes ikke hendelse denne bussen"
                      />
                    )}
                  </Module>
                </Box>
                <Module title="Logg">
                  <OperationalLog
                    vehicleId={vehicle.id}
                    logs={vehicle.logs}
                    distributionStopPlaces={distributionStopPlaces}
                    disableInternalScroll
                    disablePadding
                  />
                </Module>
                <Module title="GPS-data">
                  <BusGps vehicle={vehicle} />
                </Module>
              </VStack>
            </ScrollModuleGrid>
          </PanelScrollArea>
        </ScrollContent>
      </HorizontalScroll>
    </BusModule>
  );
};

export default AlternativeTransportLeftPanel;
